import LoadingButton from '@mui/lab/LoadingButton';
import { Button, LinearProgress, Stack, Typography } from '@mui/material';
import { useSnackbar } from 'notistack';
import { useState } from 'react';
import useSubscriber, { OnlyFansSubscriber } from '../../hooks/useSubscriber';
import { OnlyFansUserT } from '../../hooks/useSubscriberUsernameSearchT';
import useSubscriberUsernameSearchX from '../../hooks/useSubscriberUsernameSearchX';
import DialogAdaptive from '../common/DialogAdaptive';
import DialogContentAdaptive from '../common/DialogContentAdaptive';
import OnlyFansUsernameInputT from '../common/OnlyFansUsernameInputT';
import StyledCardTitleBar from '../common/StyledCardTitleBar';
import StyledDialogActions from '../common/StyledDialogActions';

interface Props {
    subscriber: OnlyFansSubscriber;
    open: boolean;
    handleClose: () => void;
}

const VaultUploadSettingsDialog = (props: Props) => {
    const { subscriber, open, handleClose } = props;
    const { enqueueSnackbar } = useSnackbar();

    const [onlyFansUser, setOnlyFansUser] = useState<OnlyFansUserT.Response | null>(
        subscriber.messageUsers?.uploadMediaDesignatedUser
            ? {
                  id: subscriber.messageUsers.uploadMediaDesignatedUser.id,
                  username: subscriber.messageUsers.uploadMediaDesignatedUser.username,
                  isVerified: true,
                  name: '',
                  view: 't',
                  avatar: '',
              }
            : null,
    );

    const { data: userInfo, isLoading: isUserInfoLoading } = useSubscriberUsernameSearchX(onlyFansUser ? onlyFansUser.username : null);

    const { setServiceSettings, setServiceRunningLoading, refetch } = useSubscriber();

    const handleSaveSettings = async () => {
        try {
            await setServiceSettings('messageUsers.uploadMediaDesignatedUser', {
                id: onlyFansUser?.id || null,
                username: onlyFansUser?.username || null,
            });

            await refetch();

            enqueueSnackbar('Vault upload settings saved successfully', { variant: 'success' });
        } catch (error) {
            console.error('Error saving vault upload settings:', error);

            enqueueSnackbar('Failed to save vault upload settings', { variant: 'error' });
        }

        handleClose();
    };

    return (
        <DialogAdaptive maxWidth="sm" fullWidth open={open} onClose={handleClose}>
            <StyledCardTitleBar title="Upload Settings" />

            <DialogContentAdaptive>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    To upload content to the OnlyFans Vault, the media needs to be sent to another user in a direct message. Please select a
                    trusted OnlyFans user to whom the media will be sent temporarily during the upload process.
                </Typography>

                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    This could be one of your own secondary accounts or a close friend whom you are following. The media will not be visible
                    to them for long as it will be unsent shortly after the upload is complete.
                </Typography>

                <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1 }}>
                    Select OnlyFans User
                </Typography>
                <OnlyFansUsernameInputT
                    selectedUser={onlyFansUser}
                    setSelectedUser={setOnlyFansUser}
                    disallowedUsernames={[subscriber.username]}
                />
                {isUserInfoLoading && (
                    <Stack direction="column" alignItems="center" sx={{ mt: 2 }}>
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                            Checking user...
                        </Typography>
                        <LinearProgress sx={{ width: '100%', mt: 1 }} />
                    </Stack>
                )}
                {onlyFansUser && userInfo && userInfo.subscribedBy && userInfo.canReceiveChatMessage && (
                    <Typography variant="body2" color="success.main" sx={{ mt: 2 }}>
                        Valid user selected
                    </Typography>
                )}
                {onlyFansUser && userInfo && !userInfo.subscribedBy && (
                    <Typography variant="body2" color="error.main" sx={{ mt: 2 }}>
                        You are not following this user and cannot send them messages. Please select a different user.
                    </Typography>
                )}
            </DialogContentAdaptive>

            <StyledDialogActions>
                <Button onClick={handleClose} color="primary" variant="contained">
                    Close
                </Button>
                <LoadingButton
                    onClick={handleSaveSettings}
                    color="secondary"
                    variant="outlined"
                    disabled={onlyFansUser && !userInfo ? true : false}
                    loading={setServiceRunningLoading}
                >
                    <span>Save Settings</span>
                </LoadingButton>
            </StyledDialogActions>
        </DialogAdaptive>
    );
};

export default VaultUploadSettingsDialog;
